/* eslint-disable max-lines */
/* eslint-disable new-cap */
/* eslint-disable no-use-before-define */
import CalendarApi from '../../apis/calendar';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { resettingGoogleCalendarLoader } from '../slices/calendar';
import { RootState } from '../store';
import {
    CalendarEventLinkParams,
    CalendarMeetingParams,
    CreateNotificationConfirmationParams,
    CreateNotificationScheduledParams,
    SaveCustomHoursParams,
    UpdateCalendarLinkParams,
    UpdateHoursParams
} from '../../params/redux/calendar';

export const fetchCalendarReminderThunk = createAsyncThunk(
    'calendar/fetch-reminders',
    async (
        client_id: number,
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchCalendarReminders(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.reminders;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const fetchGoogleEventsThunk = createAsyncThunk(
    'calendar/fetch-events',
    async (user_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchEvents(state.app.token, user_id);
            if (response?.success === 1) {
                return response?.data;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const createEventLinkThunk = createAsyncThunk(
    'calendar/create-event',
    async (data: CalendarEventLinkParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.createLink(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchGoogleEventsThunk(data.user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const createGoogleMeetingThunk = createAsyncThunk(
    'calendar/create-meeting',
    async (data: CalendarMeetingParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.createMeeting(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchGoogleEventsThunk(data.user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const fetchDaysThunk = createAsyncThunk(
    'calendar/fetch-days',
    async (user_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchDays(state.app.token, user_id);
            if (response?.success === 1) {
                return response?.data;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const saveDaysThunk = createAsyncThunk(
    'calendar/save-days',
    async (user_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.saveDays(state.app.token, user_id);
            if (response?.success === 1) {
                await dispatch(fetchDaysThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const activeInactiveDayThunk = createAsyncThunk(
    'calendar/active-inactive-day',
    async ({ user_id, day_id, status }: { user_id: number, day_id: number, status: 'active' | 'inactive' }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.activeInactiveDay(state.app.token, day_id, status);
            if (response?.success === 1) {
                await dispatch(fetchDaysThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);


export const updateTimeThunk = createAsyncThunk(
    'calendar/update-time',
    async (data: UpdateHoursParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.updateTimes(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchDaysThunk(data.user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const updateTimeZoneThunk = createAsyncThunk(
    'calendar/update-time-zone',
    async ({ time_zone, user_id }: { time_zone: string, user_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.updateTimeZone(state.app.token, time_zone, user_id);
            if (response?.success === 1) {
                await dispatch(fetchDaysThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const fetchCustomTimeOfDateThunk = createAsyncThunk(
    'calendar/fetch-custom-time',
    async ({ date, month, user_id }: { date: string, month: string, user_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchCustomTimeOfDate(state.app.token, date, month, user_id);
            if (response?.success === 1) {
                return response;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const updateCustomTimeOfDateThunk = createAsyncThunk(
    'calendar/update-custom-time',
    async (data: SaveCustomHoursParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.updateCustomTimeOfDate(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchCustomTimeOfDateThunk({ date: data.date, month: data.month, user_id: data.user_id })).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const fetchAllTimeZonesThunk = createAsyncThunk(
    'calendar/fetch-time-zones',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchAllTimeZones(state.app.token);
            if (response?.success === 1) {
                return response?.timezones;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const fetchUserTimeZoneThunk = createAsyncThunk(
    'calendar/user-time-zone',
    async (user_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchUserTimeZone(state.app.token, user_id);
            if (response?.success === 1) {
                if (response?.timezone === null) {
                    await dispatch(udpateUserTimeZoneThunk({
                        user_id,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone as string
                    })).unwrap();
                }
                return response?.timezone;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const udpateUserTimeZoneThunk = createAsyncThunk(
    'calendar/update-user-time-zone',
    async ({ user_id, timezone }: { user_id: number, timezone: string }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.updateUserTimeZone(state.app.token, user_id, timezone);
            if (response?.success === 1) {
                await dispatch(fetchUserTimeZoneThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const enableDisabledCustomHourThunk = createAsyncThunk(
    'calendar/update-custom-time',
    async ({ user_id, month, date, status }: {
        user_id: number,
        month: string,
        date: string;
        status: string;
    }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.enableDisabledCustomHour(state.app.token, user_id, month, date, status);
            if (response?.success === 1) {
                await dispatch(fetchCustomTimeOfDateThunk({ date, month, user_id })).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const removeTimeThunk = createAsyncThunk(
    'calendar/remove-time',
    async (
        { hour_id, user_id, date, month }: { hour_id: number, user_id: number, date?: string | null, month: string | null },
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.removeTime(state.app.token, hour_id);
            if (response?.success === 1) {
                if (date && month) {
                    await dispatch(fetchCustomTimeOfDateThunk({
                        user_id,
                        date,
                        month
                    })).unwrap();
                } else {
                    await dispatch(fetchDaysThunk(user_id)).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const updateCalendarFormJSONThunk = createAsyncThunk(
    'calendar/update-json',
    async (
        { calendar_id, form_json, user_id }: { calendar_id: number, form_json: string, user_id: number },
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.saveCalendarMeetingFormJSON(state.app.token, calendar_id, form_json);
            if (response?.success === 1) {
                await dispatch(fetchGoogleEventsThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);


export const fetchCalendarLinkNotificaitonThunk = createAsyncThunk(
    'calendar/fetch-calendar-link-notification',
    async (calendar_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchCalendarLinkNotificaiton(state.app.token, calendar_id);
            if (response?.success === 1) {
                return response?.data;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const createCalendarLinkNotificationConfirmationThunk = createAsyncThunk(
    'calendar/create-calendar-link-notification-confirmation',
    async (
        data: CreateNotificationConfirmationParams,
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.createCalendarLinkNotificationConfirmation(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchCalendarLinkNotificaitonThunk(data.calendar_id as number)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const createCalendarLinkNotificationSchedulingThunk = createAsyncThunk(
    'calendar/create-calendar-link-notification-scheduling',
    async (
        data: CreateNotificationScheduledParams,
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.createCalendarLinkNotificationScheduling(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchCalendarLinkNotificaitonThunk(data.calendar_id as number)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const deleteCalendarLinkNotificationThunk = createAsyncThunk(
    'calendar/delete-calendar-link-notification',
    async (
        { calendar_id, notification_id }: { calendar_id: number, notification_id: number },
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.deleteCalendarLinkNotification(state.app.token, calendar_id, notification_id);
            if (response?.success === 1) {
                await dispatch(fetchCalendarLinkNotificaitonThunk(calendar_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const fetchMeetingNotificaitonsThunk = createAsyncThunk(
    'calendar/fetch-meeting-notification',
    async (event_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchMeetingNotificaitons(state.app.token, event_id);
            if (response?.success === 1) {
                return response?.data;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const createMeetingNotificationConfirmationThunk = createAsyncThunk(
    'calendar/create-meeting-notification-confirmation',
    async (data: CreateNotificationConfirmationParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.createMeetingNotificationConfirmation(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchMeetingNotificaitonsThunk(data.event_id as number)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const createMeetingNotificationSchedulingThunk = createAsyncThunk(
    'calendar/create-meeting-notification-scheduling',
    async (data: CreateNotificationScheduledParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.createMeetingNotificationScheduling(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchMeetingNotificaitonsThunk(data.event_id as number)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const deleteMeetingNotificaionThunk = createAsyncThunk(
    'calendar/delete-meeting-notification',
    async (
        { event_id, notification_id }: { event_id: number, notification_id: number },
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.deleteMeetingNotificaion(state.app.token, event_id, notification_id);
            if (response?.success === 1) {
                await dispatch(fetchMeetingNotificaitonsThunk(event_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const deleteCalendarLinkThunk = createAsyncThunk(
    'calendar/delete-calendar-link',
    async (
        { event_id, user_id }: { event_id: number, user_id: number },
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.deleteCalendarLink(state.app.token, event_id);
            if (response?.success === 1) {
                await dispatch(fetchGoogleEventsThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const deleteMeetingThunk = createAsyncThunk(
    'calendar/delete-meeting',
    async (
        { event_id, user_id }: { event_id: number, user_id: number },
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.deleteMeeting(state.app.token, event_id);
            if (response?.success === 1) {
                await dispatch(fetchGoogleEventsThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);


export const updateCalenderLinkThunk = createAsyncThunk(
    'calendar/update-calendar-link',
    async (
        { data, user_id }: { data: UpdateCalendarLinkParams, user_id: number },
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.editCalendarLink(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchGoogleEventsThunk(user_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);

export const fetchRecentMeetingsThunk = createAsyncThunk(
    'calendar/recent-meetings',
    async (
        _,
        { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CalendarApi.fetchRecentMeetings(state.app.token);
            if (response?.success === 1) {
                return response?.recent_meetings;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingGoogleCalendarLoader());
        }
    }
);